/**
 * iter647 — Print History dashboard.
 *
 * Visibility into every print job across every branch:
 *   - Live counters (Total / Pending / Claimed / Done / Failed).
 *   - Filters: status · branch · time window.
 *   - Row actions: re-print (creates a fresh pending job), inspect payload.
 *   - Auto-refresh every 10 s so a manager watching this page sees jobs
 *     flow through in near-real-time.
 */
import { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Printer, RefreshCw, RotateCcw, Eye, Search, AlertCircle, CheckCircle2, Clock, Hourglass } from 'lucide-react';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';

const STATUS_META = {
  pending:  { label: 'Pending', cls: 'bg-amber-100 text-amber-800',  icon: Hourglass },
  claimed:  { label: 'Printing', cls: 'bg-sky-100 text-sky-800',     icon: Clock },
  done:     { label: 'Done',    cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  failed:   { label: 'Failed',  cls: 'bg-rose-100 text-rose-800',    icon: AlertCircle },
};

function relTime(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.max(0, Math.floor(ms / 1000))}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

export default function PrintHistoryPage() {
  const branches = useBranchStore(s => s.branches);
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({ total: 0, pending: 0, claimed: 0, done: 0, failed: 0 });
  const [status, setStatus] = useState('all');
  const [branchId, setBranchId] = useState('');
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(false);
  const [inspect, setInspect] = useState(null);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ hours: String(hours), limit: '500' });
      if (status !== 'all') params.set('status', status);
      if (branchId) params.set('branch_id', branchId);
      const { data } = await api.get(`/cashier/print-bridge/history?${params.toString()}`);
      setJobs(data.jobs || []);
      setCounts(data.counts || { total: 0, pending: 0, claimed: 0, done: 0, failed: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchJobs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, branchId, hours]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // iter647 — Auto-refresh every 10s; pauses when the document is hidden.
    const id = setInterval(() => { if (!document.hidden) fetchJobs(); }, 10_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, branchId, hours]);

  const retryJob = async (job) => {
    try {
      await api.post(`/cashier/print-bridge/jobs/${job.id}/retry`);
      toast.success('Re-queued — the Bridge will pick it up on the next poll');
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    }
  };

  const recoverStuck = async () => {
    try {
      const { data } = await api.post('/cashier/print-bridge/jobs/recover-stuck');
      toast.success(data.recovered ? `Recovered ${data.recovered} stuck job(s)` : 'No stuck jobs');
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recover failed');
    }
  };

  const branchName = (id) => (branches || []).find(b => b.id === id)?.name || (id ? id.slice(0, 6) : '—');

  const filteredCount = useMemo(() => jobs.length, [jobs]);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5" data-testid="print-history-page">
        <header className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-stone-700 to-stone-900 flex items-center justify-center shadow-md">
              <Printer size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit">Print History</h1>
              <p className="text-sm text-muted-foreground">Every receipt + KOT printed across all your branches.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={recoverStuck} data-testid="recover-stuck-btn" title="Re-queue jobs claimed for more than 60s">
              <AlertCircle size={14} className="mr-1.5" /> Recover stuck
            </Button>
            <Button variant="outline" size="sm" onClick={fetchJobs} disabled={loading} data-testid="refresh-btn">
              <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </header>

        {/* Counter pills */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5" data-testid="counter-row">
          <CounterCard label="Total" value={counts.total} testId="counter-total" />
          <CounterCard label="Pending" value={counts.pending} cls="text-amber-700" testId="counter-pending" />
          <CounterCard label="Printing" value={counts.claimed} cls="text-sky-700" testId="counter-claimed" />
          <CounterCard label="Done" value={counts.done} cls="text-emerald-700" testId="counter-done" />
          <CounterCard label="Failed" value={counts.failed} cls="text-rose-700" testId="counter-failed" />
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-3 flex items-center gap-2 flex-wrap" data-testid="filter-row">
            <div className="flex items-center gap-1 text-xs">
              <label className="text-muted-foreground">Status:</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-8 px-2 rounded border border-input bg-background text-xs"
                data-testid="status-filter"
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="claimed">Printing</option>
                <option value="done">Done</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <label className="text-muted-foreground">Branch:</label>
              <select
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                className="h-8 px-2 rounded border border-input bg-background text-xs"
                data-testid="branch-filter"
              >
                <option value="">All branches</option>
                {(branches || []).map(b => (<option key={b.id} value={b.id}>{b.name}</option>))}
              </select>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <label className="text-muted-foreground">Window:</label>
              <select
                value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                className="h-8 px-2 rounded border border-input bg-background text-xs"
                data-testid="window-filter"
              >
                <option value={1}>Last hour</option>
                <option value={24}>Last 24h</option>
                <option value={168}>Last 7 days</option>
                <option value={720}>Last 30 days</option>
              </select>
            </div>
            <span className="ml-auto text-[11px] text-muted-foreground">
              Showing <strong className="tabular-nums">{filteredCount}</strong> of {counts.total}
            </span>
          </CardContent>
        </Card>

        {/* Jobs table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="jobs-table">
              <thead>
                <tr className="bg-stone-50 dark:bg-stone-900/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="text-left px-3 py-2 font-semibold">When</th>
                  <th className="text-left px-3 py-2 font-semibold">Branch</th>
                  <th className="text-left px-3 py-2 font-semibold">Bridge / Printer</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                  <th className="text-left px-3 py-2 font-semibold">Retries</th>
                  <th className="text-right px-3 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(j => {
                  const meta = STATUS_META[j.status] || STATUS_META.pending;
                  const Ico = meta.icon;
                  return (
                    <tr key={j.id} className="border-t border-border/60 hover:bg-stone-50/60 dark:hover:bg-stone-900/40" data-testid={`job-row-${j.id}`}>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-medium">{relTime(j.created_at)}</div>
                        <div className="text-[10px] text-muted-foreground tabular-nums">{j.created_at?.replace('T', ' ').slice(0, 19)}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{branchName(j.branch_id)}</td>
                      <td className="px-3 py-2 text-[11px]">
                        <div className="font-mono truncate max-w-[230px]">{j.bridge_id?.slice(0, 8) || '—'}</div>
                        <div className="text-muted-foreground tabular-nums">{j.printer_ip || '—'}:{j.printer_port || 9100}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${meta.cls}`}>
                          <Ico size={10} /> {meta.label}
                        </span>
                        {j.error && <div className="text-[10px] text-rose-700 mt-0.5 truncate max-w-[180px]" title={j.error}>{j.error}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs tabular-nums">{j.retries || 0}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setInspect(j)} title="Inspect payload" data-testid={`inspect-${j.id}`}>
                          <Eye size={13} />
                        </Button>
                        {(j.status === 'failed' || j.status === 'claimed') && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-primary" onClick={() => retryJob(j)} title="Re-print" data-testid={`retry-${j.id}`}>
                            <RotateCcw size={13} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {jobs.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-16 text-center text-sm text-muted-foreground">
                    No print jobs in this window. <Search size={12} className="inline ml-0.5" />
                  </td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Inspect drawer (mini) */}
        {inspect && (
          <div className="fixed inset-0 bg-black/40 z-40 flex items-end sm:items-center sm:justify-end" onClick={() => setInspect(null)} data-testid="inspect-overlay">
            <div className="bg-white dark:bg-stone-900 w-full sm:max-w-md max-h-[80dvh] sm:h-full overflow-y-auto p-5 space-y-3 sm:rounded-l-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h3 className="font-bold font-outfit">Job {inspect.id.slice(0, 8)}</h3>
                <Button size="sm" variant="ghost" onClick={() => setInspect(null)}>✕</Button>
              </div>
              <div className="text-xs space-y-1.5">
                <div><strong>Status:</strong> {inspect.status}</div>
                <div><strong>Created:</strong> {inspect.created_at}</div>
                <div><strong>Branch:</strong> {branchName(inspect.branch_id)}</div>
                <div><strong>Bridge ID:</strong> <code className="text-[10px]">{inspect.bridge_id}</code></div>
                <div><strong>Printer:</strong> {inspect.printer_ip}:{inspect.printer_port || 9100}</div>
                {inspect.error && <div className="text-rose-700"><strong>Error:</strong> {inspect.error}</div>}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Payload</div>
                <pre className="bg-stone-50 dark:bg-stone-800 p-2 rounded text-[10px] overflow-x-auto max-h-72">{JSON.stringify(inspect.payload, null, 2)}</pre>
              </div>
              {(inspect.status === 'failed' || inspect.status === 'claimed') && (
                <Button onClick={() => { retryJob(inspect); setInspect(null); }} className="w-full" data-testid="inspect-retry">
                  <RotateCcw size={14} className="mr-1.5" /> Re-print this job
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function CounterCard({ label, value, cls = 'text-stone-800 dark:text-stone-200', testId }) {
  return (
    <div className="bg-white dark:bg-muted rounded-xl p-3 shadow-sm border border-border/60" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
